import { useState, useEffect, useCallback } from "react";

const carouselPhotos = [
  "https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/bb0f919b70c52b69fc3514aa7488afc3.jpeg",
  "https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/10a1e6c27177109b52f6820a41edc3f6.jpeg",
];

export default function BillyOwner() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrent(index);
      setTimeout(() => setIsAnimating(false), 500);
    },
    [isAnimating]
  );

  const prev = () => goTo((current - 1 + carouselPhotos.length) % carouselPhotos.length);
  const next = () => goTo((current + 1) % carouselPhotos.length);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % carouselPhotos.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  return (
    <section id="billy-owner" className="relative bg-gradient-to-b from-white via-[#F6D7DF]/10 to-white py-20 md:py-28">
      {/* Hero Image with Title Overlay */}
      <div className="max-w-[1400px] mx-auto px-6 mb-12">
        <div className="relative h-[600px] md:h-[700px] rounded-3xl overflow-hidden shadow-2xl">
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/e130bdb0459c46b2cac2b8c2174a1068.jpeg"
              alt="Billy Kemp IV"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2B6777]/95 via-[#2B6777]/60 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-[#2B6777]/40 via-transparent to-transparent"></div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-10 md:p-14">
            <div className="max-w-[800px]">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-md rounded-full mb-4 border border-white/30 shadow-lg">
                <div className="w-2 h-2 rounded-full bg-[#52AB98] animate-pulse"></div>
                <span className="text-sm font-medium text-white uppercase tracking-wide">
                  Founder's Story
                </span>
              </div>
              <h2 className="text-5xl md:text-6xl font-heading font-bold text-white mb-3 tracking-tight leading-tight">
                Meet Billy Kemp IV
              </h2>
              <p className="text-2xl md:text-3xl text-white/95 font-light tracking-wide">
                A Personal Note from the Founder
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Photo Carousel */}
      <div className="max-w-[1400px] mx-auto px-6 mb-20">
        <div className="relative rounded-3xl overflow-hidden shadow-2xl" style={{ height: "520px" }}>
          {/* Slides */}
          {carouselPhotos.map((photo, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-500 ${
                index === current ? "opacity-100 z-10" : "opacity-0 z-0"
              }`}
            >
              <img
                src={photo}
                alt={`Gallery photo ${index + 1}`}
                className="w-full h-full object-cover object-center"
              />
            </div>
          ))}

          {/* Dark overlay for arrows */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent z-20 pointer-events-none"></div>

          {/* Prev Arrow */}
          <button
            onClick={prev}
            className="absolute left-5 top-1/2 -translate-y-1/2 z-30 w-12 h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white hover:bg-white/40 transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          {/* Next Arrow */}
          <button
            onClick={next}
            className="absolute right-5 top-1/2 -translate-y-1/2 z-30 w-12 h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white hover:bg-white/40 transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>

          {/* Dot Indicators */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
            {carouselPhotos.map((_, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  index === current
                    ? "w-8 h-3 bg-white"
                    : "w-3 h-3 bg-white/50 hover:bg-white/80"
                }`}
              />
            ))}
          </div>

          {/* Photo counter */}
          <div className="absolute top-5 right-5 z-30 px-3 py-1 bg-black/30 backdrop-blur-md rounded-full text-white text-sm font-medium">
            {current + 1} / {carouselPhotos.length}
          </div>
        </div>
      </div>

      {/* Story Content Section */}
      <div className="max-w-[1000px] mx-auto px-6">
        <div className="space-y-8">
          {/* Quote 1 */}
          <div className="relative group">
            <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#52AB98] to-[#2B6777] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative bg-white rounded-2xl p-8 md:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 border border-[#2B6777]/5">
              <div className="absolute -top-4 left-8 w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-[#52AB98] to-[#2B6777] shadow-lg">
                <i className="ri-double-quotes-l text-white text-xl"></i>
              </div>
              <div className="pt-4">
                <p className="text-xl md:text-2xl text-[#2B6777] leading-relaxed font-light">
                  "This moment means everything to me. Not just because I'm launching my first company — but because I'm continuing a legacy and building something I truly believe will change the way homeowners experience window replacement."
                </p>
              </div>
            </div>
          </div>

          {/* Quote 2 */}
          <div className="relative group">
            <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#2B6777] to-[#52AB98] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative bg-white rounded-2xl p-8 md:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 border border-[#2B6777]/5">
              <div className="absolute -top-4 left-8 w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-[#2B6777] to-[#52AB98] shadow-lg">
                <i className="ri-double-quotes-l text-white text-xl"></i>
              </div>
              <div className="pt-4">
                <p className="text-xl md:text-2xl text-[#2B6777] leading-relaxed font-light">
                  "I played football at the highest level. That world taught me discipline, resilience, and how to lead under pressure. I've taken that same mentality — and built something new."
                </p>
              </div>
            </div>
          </div>

          {/* Quote 3 */}
          <div className="relative group">
            <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#52AB98] to-[#F6D7DF] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative bg-gradient-to-br from-[#2B6777]/5 to-[#52AB98]/5 rounded-2xl p-8 md:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 border border-[#2B6777]/10">
              <div className="absolute -top-4 left-8 w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-[#F6D7DF] to-[#52AB98] shadow-lg">
                <i className="ri-double-quotes-l text-white text-xl"></i>
              </div>
              <div className="pt-4">
                <p className="text-xl md:text-2xl text-[#2B6777] leading-relaxed font-medium">
                  "But before I talk about technology, I have to talk about where this all started."
                </p>
              </div>
            </div>
            <div className="flex justify-center mt-6">
              <img
                src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/174935118bcb55f4c053eab01879f4cc.png"
                alt="University of Nebraska Logo"
                className="w-20 h-20 object-contain"
              />
            </div>
          </div>
        </div>

        <div className="mt-16 flex justify-center">
          <div className="w-16 h-1 bg-gradient-to-r from-transparent via-[#52AB98] to-transparent rounded-full"></div>
        </div>
      </div>

      <div className="absolute top-20 right-10 w-64 h-64 bg-[#52AB98]/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-[#F6D7DF]/10 rounded-full blur-3xl pointer-events-none"></div>
    </section>
  );
}
